import { useMemo, useRef, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle, LineChart, StackedBarChart } from '@mesh.ai/rabbit';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import { getIntervalCount } from 'Deer/helpers/intervalMonthCalculator';
import { GOAL_STATUS_NODES } from 'Deer/hooks/dataHooks/constants';
import { useGetOverviewMetrics } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetrics';
import { useGetOverviewMetricsExplanations } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetricsExplanations';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { MODULE_KEYS_METRIC_CARDS_DEER } from 'Deer/metricCards/constants';
import { getGoalOverviewMetricCardPersistenceKey } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/cards/Goals/DirectReportsGoalsOverview';
import { DeerBarChart } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/cards/common/DeerBarChart';
import { DeerLineChart } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/cards/common/DeerLineChart';
import { transformedCompanyCardsForGoals } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/utlis';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { useSelectVisierAuthToken } from 'Deer/slice/UserAccessesSlice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGoalPreferencesContext } from 'screens/admin/Objectives/GoalPreferences/context/GoalPreferencesProvider';
import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { checkSandboxStaticDataFeaturesEnabled, checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

// import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { modifyString, StringModifier } from 'constants/copyHelpers';

import { staticBarChartData, staticCardsData, staticLineChartData } from './goalsMockData';

export const CompanyGoalsOverview = () => {
  const history = useHistory();

  const dispatch = useDispatch();
  const barChartRef = useRef();
  const lineChartRef = useRef();
  const { moduleName } = useOrganisationSettings();
  const visierAuthToken = useSelectVisierAuthToken();
  const currentURL = history.location.pathname;
  const { data: timelineData } = useTimeBoundTimePeriods();

  const { uuid: currentTimelineUuId } = timelineData?.current ?? {};

  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'myCompanyGoalsOverview' });

  const [correlationId] = useState(window.crypto.randomUUID());

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'goalsOverview',
    section: 'myCompany',
  });

  const currentDate = new Date();

  // @ts-expect-error - goalPreferenceDetails is not defined in the type
  const { goalPreferenceDetails = {} } = useGoalPreferencesContext();

  // Format the date in the desired format
  const formattedDate = format(currentDate, 'yyyy-MM-dd');

  const { data, isError, isLoading, isFetched } = useGetOverviewMetrics<
    ['GOAL_COUNT_BY_STATUS', 'ACTIVE_GOAL_PROGRESS_AVERAGE']
  >({
    metrics: [
      {
        analyticsType: 'GOAL',
        metricType: 'GOAL_COUNT_BY_STATUS',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: getIntervalCount(goalPreferenceDetails?.startMonth),
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
      {
        analyticsType: 'GOAL',
        metricType: 'ACTIVE_GOAL_PROGRESS_AVERAGE',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: getIntervalCount(goalPreferenceDetails?.startMonth),
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
    ],
    group: 'GOAL',
    correlationId,
    cohortId: -1,
  });

  const { data: kpiCardsData } = useGetOverviewMetrics<
    [
      'ACTIVE_GOAL_PROGRESS_AVERAGE_KPI',
      'EMP_COUNT_NO_GOALS_SET',
      'EMP_COUNT_NO_GOAL_CHECKINS',
      'EMP_COUNT_5_GOALS_OR_MORE',
    ]
  >({
    metrics: [
      {
        analyticsType: 'GOAL',
        metricType: 'ACTIVE_GOAL_PROGRESS_AVERAGE_KPI',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: 1,
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
      {
        analyticsType: 'GOAL',
        metricType: 'EMP_COUNT_NO_GOAL_CHECKINS',
        params: {
          timeBoundDetailsId: 1325,
        },
      },
      {
        analyticsType: 'GOAL',
        metricType: 'EMP_COUNT_NO_GOALS_SET',
        params: {
          timeBoundDetailsId: currentTimelineUuId,
        },
      },
      {
        analyticsType: 'GOAL',
        metricType: 'EMP_COUNT_5_GOALS_OR_MORE',
        params: {
          timeBoundDetailsId: currentTimelineUuId,
        },
      },
    ],
    cohortId: -1,
  });

  const { data: explanationData } = useGetOverviewMetricsExplanations({
    cohortId: -1,
    group: 'GOAL',
    correlationId,
    enabled: isFetched,
  });

  const barChartData = useMemo(() => data && data.GOAL_COUNT_BY_STATUS, [data]);

  const lineChartData = useMemo(() => data && data.ACTIVE_GOAL_PROGRESS_AVERAGE, [data]);

  const cardsDataFromApi = useMemo(
    () =>
      kpiCardsData &&
      transformedCompanyCardsForGoals({
        ACTIVE_GOAL_PROGRESS_AVERAGE_KPI: kpiCardsData.ACTIVE_GOAL_PROGRESS_AVERAGE_KPI,
        EMP_COUNT_NO_GOAL_CHECKINS: kpiCardsData.EMP_COUNT_NO_GOAL_CHECKINS,
        EMP_COUNT_NO_GOALS_SET: kpiCardsData.EMP_COUNT_NO_GOALS_SET,
        EMP_COUNT_5_GOALS_OR_MORE: kpiCardsData.EMP_COUNT_5_GOALS_OR_MORE,
      }),
    [kpiCardsData]
  );

  const explanation: {
    explanation?: string;
    sentiment?: 'warning' | 'success';
  } = useMemo(
    () =>
      explanationData && explanationData[0]
        ? {
            explanation: explanationData[0].explanation,
            sentiment: explanationData[0].sentiment === 'negative' ? 'warning' : 'success',
          }
        : {},
    [explanationData]
  );

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'goalsOverview',
        section: 'myCompany',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  return (
    <OverviewSection
      isError={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? false : !visierAuthToken
      }
      open={isCardOpen}
      onOpenChange={handleChange}
      title={`${modifyString(moduleName, StringModifier.TITLE_CASE)}s`}
      topSection={
        <MetricCards
          columns={`repeat(${
            checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
              ? staticCardsData?.length
              : cardsDataFromApi?.length
          }, 1fr)`}
          cards={
            checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
              ? staticCardsData
              : cardsDataFromApi
          }
          cardTitle="Metrics"
          module={MODULE_KEYS_METRIC_CARDS_DEER.GOAL}
          gridStatePersistenceKey={getGoalOverviewMetricCardPersistenceKey({
            tabId: 'Insights',
            viewId: '-1',
            uniqueId: 'GroupByIndividualGrid',
          })}
          isOverviewPageCard
        />
      }
      showCaption={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() || explanation?.explanation
      }
      subTitleColor={explanation.sentiment ?? 'success'}
      emptyStateDescription="Create, own or collaborate on goals to unlock valuable insights on your company's performance"
      subTitle={
        explanation.explanation ??
        'Your organization is making excellent progress on its goals. However, several goals have been dropped in the People and Culture function. Check in with people leaders to set achievable goals and work towards achieving them.'
      }
    >
      {/* Goals Distribution Chart */}
      <Card
        style={{
          marginBottom: '8px',
        }}
      >
        <CardHeader>
          <CardTitle>Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
            <div style={{ height: '240px' }} className="rabbit-ds">
              <StackedBarChart
                ref={barChartRef}
                mode="full"
                nodes={staticBarChartData?.nodes}
                series={staticBarChartData?.series}
                tooltipTitle="Distribution"
                xAxisLabel="Functions"
                xAxisValues={staticBarChartData?.xaxis}
                yAxisLabel="Goal count"
                seriesSelectorTitle="Status"
                maxNodesInMiniMode={0}
              />
            </div>
          ) : (
            <div style={{ height: '240px' }} className="rabbit-ds">
              <DeerBarChart
                isLoading={isLoading}
                isError={isError}
                ref={barChartRef}
                nodes={GOAL_STATUS_NODES}
                series={barChartData?.series}
                tooltipTitle="Distribution"
                xAxisLabel="Functions"
                xAxisValues={barChartData?.xAxis}
                yAxisLabel="Goal count"
                seriesSelectorTitle="Status"
                hasNoData={barChartData?.xAxis?.length === 0}
                emptyStateDescription="Create, own or collaborate on goals to unlock valuable insights on your company's performance"
              />
            </div>
          )}
        </CardContent>
      </Card>
      {/* Goals Progress Chart */}

      <Card>
        <CardHeader>
          <CardTitle>Progress</CardTitle>
        </CardHeader>
        <CardContent>
          {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
            <div style={{ height: '240px' }} className="rabbit-ds">
              <LineChart
                ref={lineChartRef}
                nodes={staticLineChartData?.nodes}
                xAxisValues={staticLineChartData?.xaxis}
                series={staticLineChartData?.series}
                xAxisLabel="Months"
                yAxisLabel="Goal progress"
                mode="full"
                connectNulls
                yAxisUnitSymbol={{
                  symbol: '%',
                  position: 'post',
                }}
                tooltipTitle="Progress"
                seriesSelectorTitle="Functions"
                maxNodesInMiniMode={0}
              />
            </div>
          ) : (
            <DeerLineChart
              isLoading={isLoading}
              isError={isError}
              ref={lineChartRef}
              nodes={lineChartData?.nodes}
              xAxisValues={lineChartData?.xAxis}
              series={lineChartData?.series}
              xAxisLabel="Months"
              yAxisLabel="Goal progress%"
              tooltipTitle="Progress"
              seriesSelectorTitle="Functions"
              preselectedNodes={10}
              hasNoData={lineChartData?.series.length === 0}
              yAxisUnitSymbol={{
                symbol: '%',
                position: 'post',
              }}
            />
          )}
        </CardContent>
      </Card>
    </OverviewSection>
  );
};
